import { useEffect, useState } from 'react'
import { Calendar, RefreshCw, Search, Loader2, Users } from 'lucide-react'
import { Button } from '@/modules/shared/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/modules/shared/components/ui/card'
import { Badge } from '@/modules/shared/components/ui/badge'
import { Input } from '@/modules/shared/components/ui/input'
import { FabComponent } from '@/modules/shared/components/fab.component'
import { BottomMenuComponent } from '@/modules/shared/components/bottom-menu.component'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/modules/shared/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/modules/shared/components/ui/popover'
import { Calendar as CalendarComponent } from '@/modules/shared/components/ui/calendar'
import { format } from 'date-fns'
import { useMainStore } from '@/modules/shared/stores/main.store'

import { RouteClientCardComponent } from '../components/route-client-card.component'
import { type Route, type RouteClient } from '../hooks/use-route-clients.hook'
import { MENU_ACTIONS } from '../constants/menu-action.constants'
import { toast } from 'sonner'
import { ptBR } from 'date-fns/locale'

// Tipo para as props da interface
interface RouteClientsInterfaceProps {
	routes: Route[]
	filteredClients: RouteClient[]
	selectedRouteId: number | null
	selectedClient: RouteClient | null
	isLoading: boolean
	error: string | null
	searchTerm: string
	selectRoute: (routeId: number) => void
	selectClient: (client: RouteClient) => void
	handleSearch: (term: string) => void
	selectFunctionToActions: (actionType: string) => void
	loadRoutes: () => void
}

export function RouteClientsInterface({
	routes,
	filteredClients,
	selectedRouteId,
	selectedClient,
	isLoading,
	error,
	searchTerm,
	selectRoute,
	selectClient,
	handleSearch,
	selectFunctionToActions,
	loadRoutes
}: RouteClientsInterfaceProps) {
	const selectedRoute = routes.find(route => route.idRota === selectedRouteId)

	const { selectedDate, setSelectedDate } = useMainStore(state => ({
		selectedDate: state.selectedDate,
		setSelectedDate: state.actions.setSelectedDate
	}))

	const [date, setDate] = useState(selectedDate || new Date())

	const handleDateSelect = (newDate) => {
		setDate(newDate)
		setSelectedDate(newDate)
	}

	const handleActionSelection = (actionType) => {
		if (
			actionType === 'startOrder' &&
			selectedClient &&
			(!selectedClient.idTabelaPrecoVigencia || selectedClient.idTabelaPrecoVigencia === '')
		) {
			toast.info('Cliente sem tabela de preço vigente.')
			return
		}

		selectFunctionToActions(actionType)
	}

	return (
		<div className="min-h-screen pb-20">
			<div className="space-y-2">
				<div className="flex flex-col sm:flex-row gap-2">
					<div className="flex-1">
						{routes && routes.length > 0 ? (
							<Select
								value={selectedRouteId?.toString() || undefined}
								onValueChange={(value) => selectRoute(Number(value))}
							>
								<SelectTrigger>
									<SelectValue placeholder="Selecione uma rota" />
								</SelectTrigger>
								<SelectContent>
									{routes.map((route) => (
										<SelectItem key={route.idRota} value={route.idRota.toString()}>
											{route.descricao}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						) : (
							<Select disabled>
								<SelectTrigger>
									<SelectValue placeholder={isLoading ? "Carregando rotas..." : "Nenhuma rota disponível"} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="placeholder">Nenhuma rota disponível</SelectItem>
								</SelectContent>
							</Select>
						)}
					</div>

					<div className="flex-none">
						<Popover>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									className="w-full justify-start text-left font-normal"
								>
									<Calendar className="mr-2 h-4 w-4" />
									{date ? format(date, 'dd/MM/yyyy') : "Selecionar data de entrega"}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0" align="end">
								<CalendarComponent
									mode="single"
									locale={ptBR}
									selected={date}
									onSelect={handleDateSelect}
									initialFocus
								/>
							</PopoverContent>
						</Popover>
					</div>
				</div>

				{selectedRouteId && (
					<div className="relative w-full">
						<Search className="absolute left-2 top-4 h-4 w-4 text-muted-foreground" />
						<Input
							className="pl-8 pr-4"
							placeholder="Buscar cliente por nome, CNPJ ou cidade..."
							value={searchTerm}
							onChange={(e) => handleSearch(e.target.value)}
						/>
					</div>
				)}
			</div>

			{!selectedRouteId ? (
				<div className="flex flex-col items-center justify-center py-8">
					<Calendar className="h-12 w-12 text-muted-foreground opacity-50" />
					<p className="mt-4 text-muted-foreground">Selecione uma rota para ver os clientes</p>
				</div>
			) : filteredClients.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-8">
					<Users className="h-12 w-12 text-muted-foreground opacity-50" />
					<p className="mt-4 text-muted-foreground">
						{searchTerm ? 'Nenhum cliente encontrado para esta busca' : 'Nenhum cliente encontrado nesta rota'}
					</p>
				</div>
			) : (
				<>
					<div className="flex items-center justify-between mt-4 mb-3 px-2">
						<p className="text-sm text-gray-600 flex items-center gap-2">
							<Users className="h-4 w-4" />
							{filteredClients.length === 1
								? `${filteredClients.length} cliente encontrado`
								: `${filteredClients.length} clientes encontrados`
							}
						</p>
						{selectedRoute && (
							<Badge variant="secondary" className="text-xs">
								{selectedRoute.descricao}
							</Badge>
						)}
					</div>
					<div className="grid md:grid-cols-3 grid-cols-1 gap-2 h-full">
						{filteredClients.map((client) => (
							<RouteClientCardComponent
								key={client.idEntidade}
								clientSelected={selectedClient}
								entity={client}
								onSelectEntity={selectClient}
							/>
						))}
					</div>
				</>
			)}

			{selectedClient ? (
				<FabComponent
					className='bottom-20 lg:bottom-4'
					items={MENU_ACTIONS.map((item) => ({
						id: item.id,
						icon: item.icon,
						type: item.type,
						label: item.label,
						disabled:
							(item.type === 'makeCall' && (!selectedClient.fone || !selectedClient.fone.trim())) ||
							(item.type === 'sendEmail' && (!selectedClient.email || !selectedClient.email.trim())),
					}))}
					action={handleActionSelection}
				/>
			) : null}

			<BottomMenuComponent />
		</div>
	)
}